import { useEffect, useMemo, useRef, useState } from 'react';
import { DT, DS2 } from '../../theme/designTokens';
import { useFriends } from '../../context/FriendContext';
import { useIsMobile } from '../../hooks/useBreakpoint';
import { PlayerAvatar } from '../../components/ui/PlayerAvatar';
import { UnreadBadge } from '../../components/ui/UnreadBadge';
import { relativeTimeShort } from '../../utils/relativeTime';
import { useTranslation } from '../../i18n/react';
import type { Conversation, FriendListItem } from '../../types/social';

const PRESENCE_COLOR: Record<FriendListItem['status'], string> = {
  online:  '#4CAF50',
  in_game: '#A855F7',
  offline: '#6B6B6B',
};

function PresenceDot({ status }: { status?: FriendListItem['status'] }) {
  if (!status) return null;
  return (
    <span
      aria-hidden="true"
      style={{
        position:     'absolute',
        right:        -1,
        bottom:       -1,
        width:        10,
        height:       10,
        borderRadius: '50%',
        background:   PRESENCE_COLOR[status],
        border:       '2px solid #0B0B0B',
      }}
    />
  );
}

function SearchIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="7" stroke="#A89F86" strokeWidth="2" />
      <path d="M20 20l-3.2-3.2" stroke="#A89F86" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function PlusIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 4v16M4 12h16" stroke="#0B0B0B" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

interface Props {
  activeFriendId: number | null;
  onSelect: (friendId: number) => void;
  fullWidth?: boolean;
}

export function ConversationList({ activeFriendId, onSelect, fullWidth }: Props) {
  const isMobile = useIsMobile();
  const { t } = useTranslation('messages');
  const { t: tc } = useTranslation('common');
  const { conversations, conversationsLoading, friends, hideConversation } = useFriends();
  const [query, setQuery] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false);
    }
    if (pickerOpen) document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [pickerOpen]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sorted = [...conversations].sort((a, b) => {
      const at = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
      const bt = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
      return bt - at;
    });
    if (!q) return sorted;
    return sorted.filter(c => c.friend.display_name.toLowerCase().includes(q));
  }, [conversations, query]);

  const pickerFriends = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    return friends.filter(f => !q || f.friend.display_name.toLowerCase().includes(q));
  }, [friends, pickerQuery]);

  const unreadTotal = conversations.reduce((sum, c) => sum + c.unread_count, 0);

  function statusFor(friendUserId: number): FriendListItem['status'] | undefined {
    return friends.find(f => f.friend.id === friendUserId)?.status;
  }

  return (
    <div
      style={{
        width:        fullWidth ? '100%' : 340,
        flexShrink:   0,
        display:      'flex',
        flexDirection: 'column',
        minHeight:    0,
        borderRight:  fullWidth ? 'none' : `1px solid ${DS2.color.cardBorder}`,
        background:   DT.color.pageBg,
      }}
    >
      <div style={{ padding: isMobile ? '18px 16px 14px' : '24px 20px 16px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', fontFamily: DS2.font.heading, fontWeight: 700, fontSize: isMobile ? 20 : 22, color: '#FFFFFF' }}>
            {/* One name for one system: the same CHAT identity as the desktop
                sidebar button and dock (Docked Chat Redesign). */}
            {t('chat.title')}
            {unreadTotal > 0 && <UnreadBadge count={unreadTotal} />}
          </h1>
          <div ref={pickerRef} style={{ position: 'relative' }}>
            <button
              type="button"
              aria-label={t('newMessage')}
              onClick={() => setPickerOpen(o => !o)}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 30, height: 30, borderRadius: '50%', border: 'none',
                background: DS2.gradient.gold, cursor: 'pointer', flexShrink: 0,
              }}
            >
              <PlusIcon />
            </button>
            {pickerOpen && (
              <div
                style={{
                  position:     'absolute',
                  top:          'calc(100% + 8px)',
                  right:        0,
                  width:        260,
                  maxHeight:    320,
                  display:      'flex',
                  flexDirection: 'column',
                  background:   '#161616',
                  border:       '1px solid rgba(201,168,76,0.3)',
                  borderRadius: 12,
                  boxShadow:    '0 12px 32px rgba(0,0,0,0.5)',
                  zIndex:       20,
                  overflow:     'hidden',
                }}
              >
                <div style={{ padding: 10, borderBottom: '1px solid rgba(245,245,245,0.08)' }}>
                  <input
                    type="text"
                    autoFocus
                    value={pickerQuery}
                    onChange={e => setPickerQuery(e.target.value)}
                    placeholder={t('findFriendPlaceholder')}
                    style={{
                      width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none',
                      background: 'rgba(255,255,255,0.08)', borderRadius: 8, padding: '8px 10px',
                      color: '#FFFFFF', fontFamily: DS2.font.body, fontSize: 13,
                    }}
                  />
                </div>
                <div style={{ overflowY: 'auto' }}>
                  {pickerFriends.length === 0 ? (
                    <div style={{ padding: '14px 16px', fontFamily: DS2.font.body, fontSize: 13, color: '#A89F86' }}>
                      {t('noFriendsFound')}
                    </div>
                  ) : (
                    pickerFriends.map(f => (
                      <button
                        type="button"
                        key={f.friendship_id}
                        onClick={() => { onSelect(f.friend.id); setPickerOpen(false); setPickerQuery(''); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                          padding: '9px 14px', background: 'transparent', border: 'none',
                          cursor: 'pointer', textAlign: 'left',
                        }}
                      >
                        <PlayerAvatar avatarKey={f.friend.avatar_key} name={f.friend.display_name} size={26} />
                        <span style={{ fontFamily: DS2.font.body, fontSize: 13.5, color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {f.friend.display_name}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(245,245,245,0.12)', borderRadius: 10, padding: '8px 12px' }}>
          <SearchIcon />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('searchPlaceholder')}
            style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', color: '#FFFFFF', fontFamily: DS2.font.body, fontSize: 13.5 }}
          />
        </label>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {conversationsLoading ? (
          <div style={{ padding: '20px', fontFamily: DS2.font.body, fontSize: 13, color: '#A89F86' }}>{tc('status.loading')}</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '20px', fontFamily: DS2.font.body, fontSize: 13, color: '#A89F86' }}>
            {conversations.length === 0 ? t('emptyConversations') : t('noSearchMatches')}
          </div>
        ) : (
          filtered.map(c => (
            <ConversationRow
              key={c.friend.id}
              conversation={c}
              active={c.friend.id === activeFriendId}
              status={statusFor(c.friend.id)}
              onClick={() => onSelect(c.friend.id)}
              onRemove={() => void hideConversation?.(c.friend.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function ConversationRow({ conversation, active, status, onClick, onRemove }: {
  conversation: Conversation; active: boolean; status?: FriendListItem['status']; onClick: () => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation('messages');
  const unread = conversation.unread_count > 0;
  // The last message BODY is user content, interpolated verbatim; only the
  // "You:" attribution and the photo/empty labels are application copy.
  const previewBody = conversation.last_message || (conversation.last_message_has_image ? t('previewPhoto') : '');
  const preview = previewBody
    ? (conversation.last_from_me ? t('previewYou', { text: previewBody }) : previewBody)
    : t('previewEmpty');
  return (
    <div
      style={{
        display:      'flex',
        alignItems:   'center',
        background:   active ? 'rgba(201,168,76,0.14)' : 'transparent',
        borderLeft:   active ? '3px solid #C9A84C' : '3px solid transparent',
        borderBottom: '1px solid rgba(245,245,245,0.06)',
      }}
    >
    <button
      type="button"
      onClick={onClick}
      style={{
        display:      'flex',
        alignItems:   'center',
        gap:          12,
        flex:         1,
        minWidth:     0,
        padding:      '12px 6px 12px 20px',
        background:   'transparent',
        border:       'none',
        cursor:       'pointer',
        textAlign:    'left',
      }}
    >
      <span style={{ position: 'relative', flexShrink: 0 }}>
        <PlayerAvatar avatarKey={conversation.friend.avatar_key} name={conversation.friend.display_name} size={42} />
        <PresenceDot status={status} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
          <span
            title={conversation.friend.display_name}
            style={{
              fontFamily: DS2.font.body, fontSize: 14.5, fontWeight: unread ? 700 : 500,
              color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {conversation.friend.display_name}
          </span>
          {conversation.last_message_at && (
            <span style={{ flexShrink: 0, fontFamily: DS2.font.body, fontSize: 11, color: unread ? '#D4AF37' : '#777777' }}>
              {relativeTimeShort(conversation.last_message_at)}
            </span>
          )}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 2 }}>
          <span
            style={{
              fontFamily: DS2.font.body, fontSize: 12.5, fontWeight: unread ? 600 : 400,
              color: unread ? '#E8E2D0' : '#8A8A8A', overflow: 'hidden', textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {preview}
          </span>
          {unread && <UnreadBadge count={conversation.unread_count} />}
        </span>
      </span>
    </button>
    {/* "Remove from list" — the same per-user hide as the desktop CHAT
        popup: only this user's list entry disappears (history intact; a
        newer message brings the conversation back). */}
    <button
      type="button"
      aria-label={`${t('chat.removeFromList')} — ${conversation.friend.display_name}`}
      title={t('chat.removeFromList')}
      onClick={onRemove}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 30, height: 30, marginRight: 10, borderRadius: 8, border: 'none',
        background: 'transparent', cursor: 'pointer', flexShrink: 0,
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <svg width={12} height={12} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M5 5l14 14M19 5L5 19" stroke="#8A8A8A" strokeWidth="2.2" strokeLinecap="round" />
      </svg>
    </button>
    </div>
  );
}
